import { useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { Link } from 'react-router-dom'
import Icon from '../../components/icons.jsx'
import TaskPanel from '../projetos/TaskPanel.jsx'
import { timelineTarget } from './timelineTarget.js'
import { atDownloadAttachment, getDealTimeline, getCrmContactTimeline, updateCrmNote, deleteCrmNote } from '../../db/api.js'
import { fmtDateTime } from './panelKit.jsx'
import './deal-timeline.css'
import PipelineChat from './PipelineChat.jsx'

const NOTE_MAX_LENGTH = 10000
const COLLAPSE_AT = 280 // notas maiores que isso começam recolhidas, com "Ver mais"

const origins = { deal: 'Negócio', deal_move: 'Negócio', deal_status: 'Negócio', lead: 'Lead', company: 'Empresa', activity: 'Lead', deal_activity: 'Negócio', note: 'Nota', message: 'Atendimento', conversation: 'Atendimento', tracker: 'Site' }

function Audio({ attachment }) {
  const [url, setUrl] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  const isAudio = attachment.mime?.startsWith('audio/')
  return <div>{url ? isAudio ? <audio controls preload="none" src={url} onError={() => { setUrl(''); setError('Não foi possível reproduzir. Tente novamente.') }} /> : <a href={url} target="_blank" rel="noopener noreferrer">Abrir {attachment.nome || 'anexo'}</a> : <button type="button" disabled={busy} onClick={async () => {
    setBusy(true); setError('')
    try { const data = await atDownloadAttachment(attachment.id); setUrl(data.url) }
    catch (e) { setError(e.message) }
    finally { setBusy(false) }
  }}>{busy ? 'Carregando…' : isAudio ? 'Reproduzir áudio' : `Carregar ${attachment.nome || 'anexo'}`}</button>}{error && <p role="alert">{error}</p>}</div>
}

function EventDetails({ item, onClose }) {
  const dialog = useRef(null)
  useEffect(() => { dialog.current?.showModal() }, [])
  return createPortal(<dialog ref={dialog} className="deal-timeline-dialog" aria-labelledby="timeline-event-title"
    onCancel={event => { event.preventDefault(); onClose() }}
    onKeyDown={event => { if (event.key === 'Escape') event.stopPropagation() }}
    onClick={event => { if (event.target === event.currentTarget) onClose() }}>
    <header><h2 id="timeline-event-title">{item.title}</h2><button type="button" aria-label="Fechar detalhes do evento" onClick={onClose}><Icon n="x" s={18}/></button></header>
    <time>{fmtDateTime(item.occurred_at)}</time>
    <p>{item.description}</p>
    <dl>{(item.details || []).filter(detail => detail.value != null && detail.value !== '' && !['Conta', 'Nota', 'Mensagem'].includes(detail.label)).map(detail => <div key={detail.label}><dt>{detail.label}</dt><dd>{detail.format === 'datetime' ? fmtDateTime(detail.value) : String(detail.value)}</dd></div>)}</dl>
  </dialog>, document.body)
}

export default function DealTimeline({ dealId, contactId, revision, relationshipRevision, unified = true, showEventActions = false, onChanged, onOpenConversation }) {
  const [selection, setSelection] = useState(null)
  const scope = !unified ? 'context' : selection && selection.dealId === dealId ? selection.scope : 'deal'
  const relatedRevision = scope === 'deal' ? null : relationshipRevision
  const eventActions = showEventActions || Boolean(dealId)
  const [conversation, setConversation] = useState(null)
  const [taskId, setTaskId] = useState(null)
  const [detailItem, setDetailItem] = useState(null)
  const [items, setItems] = useState([])
  const [cursor, setCursor] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [retry, setRetry] = useState(0)
  const [expanded, setExpanded] = useState(() => new Set())
  const [editing, setEditing] = useState(null) // { id, text, saving, error }
  const generation = useRef(0)
  const busy = useRef(false)
  useEffect(() => {
    const token = ++generation.current
    setItems([]); setCursor(null); setLoading(true); setError(''); setEditing(null); busy.current = true
    const request = contactId ? getCrmContactTimeline(contactId) : getDealTimeline(dealId, null, scope)
    request.then(data => { if (generation.current === token) { setItems(data.items); setCursor(data.next_cursor) } })
      .catch(e => { if (generation.current === token) setError(e.message) })
      .finally(() => { if (generation.current === token) { setLoading(false); busy.current = false } })
    return () => { generation.current++ }
  }, [dealId, contactId, revision, relatedRevision, retry, scope])
  const more = async () => {
    if (busy.current || !cursor) return
    const token = generation.current
    busy.current = true; setLoading(true); setError('')
    try {
      const data = await (contactId ? getCrmContactTimeline(contactId, cursor) : getDealTimeline(dealId, cursor, scope))
      if (generation.current !== token) return
      setItems(current => [...current, ...data.items.filter(item => !current.some(old => old.id === item.id))]); setCursor(data.next_cursor)
    } catch (e) { if (generation.current === token) setError(e.message) }
    finally { if (generation.current === token) { busy.current = false; setLoading(false) } }
  }
  const toggleExpanded = id => setExpanded(current => { const next = new Set(current); next.has(id) ? next.delete(id) : next.add(id); return next })
  const saveNote = async item => {
    const text = editing.text.trim()
    if (!text || editing.saving) return
    const token = generation.current
    setEditing(current => ({ ...current, saving: true, error: '' }))
    try {
      await updateCrmNote(item.id.split(':')[1], text)
      if (generation.current !== token) return
      setItems(current => current.map(old => old.id === item.id ? { ...old, description: text } : old))
      setEditing(null)
      onChanged?.()
    } catch (e) {
      if (generation.current !== token) return
      setEditing(current => current?.id === item.id ? { ...current, saving: false, error: e.message || 'Não foi possível salvar a nota.' } : current)
    }
  }
  const removeNote = async item => {
    if (editing?.saving || !window.confirm('Apagar esta nota? Essa ação não pode ser desfeita.')) return
    const token = generation.current
    setEditing(current => ({ ...current, saving: true, deleting: true, error: '' }))
    try {
      await deleteCrmNote(item.id.split(':')[1])
      if (generation.current !== token) return
      setItems(current => current.filter(old => old.id !== item.id))
      setEditing(current => current?.id === item.id ? null : current)
      onChanged?.()
    } catch (e) {
      if (generation.current !== token) return
      setEditing(current => current?.id === item.id ? { ...current, saving: false, deleting: false, error: e.message || 'Não foi possível apagar a nota.' } : current)
    }
  }
  return <div className="deal-timeline">
    {!contactId && unified && <div className="deal-timeline-filters" role="group" aria-label="Origem do histórico">
      {[['deal', 'Este negócio'], ['all', 'Todos'], ['contact', 'Contato'], ['company', 'Empresa']].map(([value, label]) =>
        <button key={value} type="button" aria-pressed={scope === value} onClick={() => setSelection({ dealId, scope: value })}>{label}</button>)}
    </div>}
    {items.map(item => {
      const source = item.id.split(':')[0]
      const target = eventActions ? timelineTarget(item, dealId) : null
      const relationship = item.relationship
      const details = (item.details || []).filter(detail => detail.value != null && detail.value !== '' && !['Conta', 'Nota', 'Mensagem', ...(relationship ? ['Lead', 'Negócio', 'Empresa'] : []), ...(relationship?.pipeline ? ['Funil', 'Funil atual'] : [])].includes(detail.label))
      const isNote = source === 'note' || item.title === 'Nota adicionada'
      const activity = ['activity', 'deal_activity'].includes(source)
      const completed = activity && /Status atual: (feito|concluido)(\s|$)/.test(item.description || '')
      const site = source === 'tracker'
      const registration = ['lead', 'company', 'deal'].includes(source) || (site && ['Clique no WhatsApp', 'Formulário enviado'].includes(item.title))
      const theme = isNote ? 'note' : completed ? 'completed' : activity ? 'activity' : registration ? 'registration' : site ? 'site' : ['message', 'conversation'].includes(source) ? 'chat' : 'movement'
      return <article key={item.id} className={`deal-timeline-event deal-timeline-${source} deal-timeline-theme-${theme}${target ? ' has-event-action' : ''}`}>
        {target && (target.href
          ? <Link className="deal-timeline-open" to={target.href} title={target.label} aria-label={target.label}><Icon n="external" s={15}/></Link>
          : <button className="deal-timeline-open" type="button" title={target.label} aria-label={target.label} onClick={() => {
            if (target.kind === 'conversation') {
              if (onOpenConversation) onOpenConversation(item.conversation, source === 'message' ? item.id.slice('message:'.length) : null)
              else setConversation(item.conversation)
            }
            else if (target.kind === 'task') setTaskId(target.id)
            else setDetailItem(item)
          }}><Icon n="external" s={15}/></button>)}
        <div className="deal-timeline-main">
        <header><h4>{item.title}</h4><span className="deal-timeline-origin">{origins[source] || item.category}</span><time>{fmtDateTime(item.occurred_at)}</time></header>
        {relationship && <div className="deal-timeline-relationship">
          {relationship.deal && <span>Negócio: <Link to={`/comercial/pipelines?deal=${encodeURIComponent(relationship.deal.id)}`}>{relationship.deal.name || 'Sem título'}</Link>{relationship.deal.id === dealId && <small> atual</small>}</span>}
          {relationship.contacts?.length > 0 && <span>{relationship.contacts.length > 1 ? 'Contatos' : 'Contato'}: {relationship.contacts.map((contact, index) => <span key={contact.id}>{index > 0 && ', '}<Link to={`/comercial/leads/${encodeURIComponent(contact.id)}`}>{contact.name || 'Sem nome'}</Link></span>)}</span>}
          {relationship.company && <span>Empresa: <Link to={`/comercial/empresas/${encodeURIComponent(relationship.company.id)}`}>{relationship.company.name || 'Sem nome'}</Link></span>}
          {relationship.pipeline && <span>{relationship.pipeline.historical ? 'Funil do evento' : 'Funil atual'}: {relationship.pipeline.name || 'Sem nome'}</span>}
        </div>}
        {editing?.id === item.id ? <div className="deal-timeline-note-edit">
          <textarea aria-label="Editar nota" rows={4} maxLength={NOTE_MAX_LENGTH} autoFocus disabled={editing.saving} value={editing.text} onChange={event => setEditing(current => ({ ...current, text: event.target.value }))} />
          <div className="deal-timeline-note-actions">
            <button type="button" disabled={editing.saving || !editing.text.trim()} onClick={() => saveNote(item)}>{editing.saving && !editing.deleting ? 'Salvando…' : 'Salvar'}</button>
            <button type="button" disabled={editing.saving} onClick={() => setEditing(null)}>Cancelar</button>
            <button type="button" className="deal-timeline-note-delete" disabled={editing.saving} onClick={() => removeNote(item)}>{editing.deleting ? 'Apagando…' : 'Apagar nota'}</button>
            <span>{editing.text.length}/{NOTE_MAX_LENGTH}</span>
          </div>
          {editing.error && <p role="alert">{editing.error}</p>}
        </div> : item.description && <>
          <p className={`deal-timeline-content${isNote && item.description.length > COLLAPSE_AT && !expanded.has(item.id) ? ' is-collapsed' : ''}`}>{item.description}</p>
          {isNote && item.description.length > COLLAPSE_AT && <button type="button" className="deal-timeline-more" aria-expanded={expanded.has(item.id)} onClick={() => toggleExpanded(item.id)}>{expanded.has(item.id) ? 'Ver menos' : 'Ver mais'}</button>}
        </>}
        {source === 'note' && editing?.id !== item.id && <button type="button" className="deal-timeline-more" onClick={() => setEditing({ id: item.id, text: item.description || '', saving: false, error: '' })}>Editar nota</button>}
        {details.length > 0 && <dl>{details.map(detail => <div key={detail.label}><dt>{detail.label}</dt><dd>{detail.format === 'datetime' ? fmtDateTime(detail.value) : typeof detail.value === 'object' ? JSON.stringify(detail.value) : String(detail.value)}</dd></div>)}</dl>}
        {(item.attachments || item.audio_attachments)?.map(attachment => <Audio key={attachment.id} attachment={attachment} />)}
        {item.conversation && !eventActions && <button type="button" onClick={() => setConversation(item.conversation)}>Abrir conversa</button>}
        </div>
      </article>
    })}
    {error && <p role="alert">{error} <button type="button" onClick={() => cursor ? more() : setRetry(n => n + 1)}>Tentar novamente</button></p>}
    {loading && <p role="status">Carregando histórico…</p>}
    {!loading && !error && !items.length && <p>Nenhum evento registrado.</p>}
    {!loading && cursor && !error && <button type="button" onClick={more}>Carregar mais eventos</button>}
    {conversation && <PipelineChat deal={{ id: dealId }} conversation={conversation} onClose={() => setConversation(null)} />}
    {taskId && <TaskPanel taskId={taskId} source="commercial" onClose={() => setTaskId(null)} onChanged={() => { setRetry(n => n + 1); onChanged?.() }} />}
    {detailItem && <EventDetails item={detailItem} onClose={() => setDetailItem(null)} />}
  </div>
}
